/**
 * Quiz Steps Registry
 * 
 * Data-driven navigation for quiz flow.
 * All step definitions, transitions, and validation rules in one place.
 * 
 * CRITICAL: Do not hardcode step transitions in screen components.
 * All navigation must go through this registry.
 */

import { lazy, ComponentType } from 'react';

// Lazy-loaded step components
const Quiz001Intro = lazy(() => import('@/pages/Quiz001Intro'));
const Quiz002Intro = lazy(() => import('@/pages/Quiz002Intro'));
const Quiz002Step2 = lazy(() => import('@/pages/Quiz002Step2'));
const Quiz002Step3 = lazy(() => import('@/pages/Quiz002Step3'));
const Quiz002Step4 = lazy(() => import('@/pages/quiz/Quiz002Step4'));
const Quiz002Step5 = lazy(() => import('@/pages/quiz/Quiz002Step5'));
const Quiz002Step6 = lazy(() => import('@/pages/quiz/Quiz002Step6'));
const Quiz002Step7 = lazy(() => import('@/pages/quiz/Quiz002Step7'));
const Quiz002Step8 = lazy(() => import('@/pages/quiz/Quiz002Step8'));
const Quiz002Step9 = lazy(() => import('@/pages/quiz/Quiz002Step9'));
const Quiz002Step10 = lazy(() => import('@/pages/quiz/Quiz002Step10'));
const Quiz002Step11 = lazy(() => import('@/pages/quiz/Quiz002Step11'));
const Quiz002Step12 = lazy(() => import('@/pages/quiz/Quiz002Step12'));
const Quiz002Step13 = lazy(() => import('@/pages/quiz/Quiz002Step13'));
const Quiz002Step14 = lazy(() => import('@/pages/quiz/Quiz002Step14'));
const Quiz002Step15 = lazy(() => import('@/pages/quiz/Quiz002Step15'));
const Quiz002Step16 = lazy(() => import('@/pages/quiz/Quiz002Step16'));
const Quiz003Processing = lazy(() => import('@/pages/quiz/Quiz003Processing'));
const Quiz004Results = lazy(() => import('@/pages/quiz/Quiz004Results'));
const Quiz005ExitDialog = lazy(() => import('@/pages/QuizExitDialog'));

/** Quiz state passed to step functions */
export interface QuizStepState {
  currentStep: number;
  answers: Record<string | number, unknown>;
  petType?: 'dog' | 'cat';
  processingStatus?: 'idle' | 'loading' | 'success' | 'error';
}

/** Progress info for progress bar */
export interface StepProgress {
  current: number;
  total: number;
  label?: string;
}

/** Step definition in registry */
export interface QuizStepDefinition {
  /** Unique step ID (e.g., 'QUIZ-002-4') */
  id: string;
  /** Human-readable title */
  title: string;
  /** Step index for main progress (1-based, null for system screens) */
  stepIndex: number | null;
  /** Progress info (null for screens without progress) */
  progress: StepProgress | null;
  /** Lazy-loaded component */
  component: ComponentType<unknown>;
  /** Validate step is complete */
  validate: (state: QuizStepState) => boolean;
  /** Get next step ID */
  next: (state: QuizStepState) => string | null;
  /** Get previous step ID (null = no back) */
  prev: (state: QuizStepState) => string | null;
  /** Route path for this step */
  route: string;
  /** Called when entering step */
  onEnter?: (state: QuizStepState) => void;
  /** Called before leaving step */
  onNext?: (state: QuizStepState) => void;
  /** Is this a system screen (modal, loader, etc.) */
  isSystem?: boolean;
  /** Category/group for this step */
  category?: 'intro' | 'main' | 'extra' | 'final' | 'system';
}

/** 
 * Main progress steps count (QUIZ-002-2 through QUIZ-002-10)
 * Used for "X из Y" counter
 */
export const MAIN_STEPS_TOTAL = 9;

/** Extra questions count (QUIZ-002-11 through QUIZ-002-13) */
export const EXTRA_STEPS_TOTAL = 3;

/** Final steps count (QUIZ-002-14 through QUIZ-002-16) */
export const FINAL_STEPS_TOTAL = 3;

/**
 * Quiz Steps Registry
 * 
 * Order matters - defines canonical flow.
 * Modify this to add/remove/reorder steps.
 */
export const QUIZ_STEPS_REGISTRY: QuizStepDefinition[] = [
  // ===== SYSTEM: Entry =====
  {
    id: 'QUIZ-001',
    title: 'Старт анкеты (дружеская анкета)',
    stepIndex: null,
    progress: null,
    component: Quiz001Intro,
    validate: () => true,
    next: () => 'QUIZ-002-1',
    prev: () => null,
    route: '/quiz/start',
    isSystem: true,
    category: 'system',
  },
  
  // ===== QUIZ-002-1: Intro with pet type selection =====
  {
    id: 'QUIZ-002-1',
    title: 'Вступление в анкету',
    stepIndex: null,
    progress: null,
    component: Quiz002Intro,
    validate: (state) => !!state.answers[0], // Pet type selected
    next: () => 'QUIZ-002-2',
    prev: () => 'QUIZ-001',
    route: '/quiz',
    category: 'intro',
  },
  
  // ===== MAIN SERIES: 9 questions =====
  {
    id: 'QUIZ-002-2',
    title: 'Вопрос 1: Предпочтения по породе',
    stepIndex: 1,
    progress: { current: 1, total: MAIN_STEPS_TOTAL },
    component: Quiz002Step2,
    validate: (state) => !!state.answers[1],
    next: () => 'QUIZ-002-3',
    prev: () => 'QUIZ-002-1',
    route: '/quiz/step/1',
    category: 'main',
  },
  {
    id: 'QUIZ-002-3',
    title: 'Вопрос 2: Пол питомца',
    stepIndex: 2,
    progress: { current: 2, total: MAIN_STEPS_TOTAL },
    component: Quiz002Step3,
    validate: (state) => !!state.answers[2],
    next: () => 'QUIZ-002-4',
    prev: () => 'QUIZ-002-2',
    route: '/quiz/step/2',
    category: 'main',
  },
  {
    id: 'QUIZ-002-4',
    title: 'Вопрос 3: Возраст питомца',
    stepIndex: 3,
    progress: { current: 3, total: MAIN_STEPS_TOTAL },
    component: Quiz002Step4,
    validate: (state) => !!state.answers[3],
    next: () => 'QUIZ-002-5',
    prev: () => 'QUIZ-002-3',
    route: '/quiz/step/3',
    category: 'main',
  },
  {
    id: 'QUIZ-002-5',
    title: 'Вопрос 4: Прогулки и выходные',
    stepIndex: 4,
    progress: { current: 4, total: MAIN_STEPS_TOTAL },
    component: Quiz002Step5,
    validate: (state) => !!state.answers[4],
    next: () => 'QUIZ-002-6',
    prev: () => 'QUIZ-002-4',
    route: '/quiz/step/4',
    category: 'main',
  },
  {
    id: 'QUIZ-002-6',
    title: 'Вопрос 5: Характер собаки',
    stepIndex: 5,
    progress: { current: 5, total: MAIN_STEPS_TOTAL },
    component: Quiz002Step6,
    validate: (state) => !!state.answers[5],
    next: () => 'QUIZ-002-7',
    prev: () => 'QUIZ-002-5',
    route: '/quiz/step/5',
    category: 'main',
  },
  {
    id: 'QUIZ-002-7',
    title: 'Вопрос 6: Время на прогулки',
    stepIndex: 6,
    progress: { current: 6, total: MAIN_STEPS_TOTAL },
    component: Quiz002Step7,
    validate: (state) => !!state.answers[6],
    next: () => 'QUIZ-002-8',
    prev: () => 'QUIZ-002-6',
    route: '/quiz/step/6',
    category: 'main',
  },
  {
    id: 'QUIZ-002-8',
    title: 'Вопрос 7: Самоописание пользователя',
    stepIndex: 7,
    progress: { current: 7, total: MAIN_STEPS_TOTAL },
    component: Quiz002Step8,
    validate: (state) => !!state.answers[7],
    next: () => 'QUIZ-002-9',
    prev: () => 'QUIZ-002-7',
    route: '/quiz/step/7',
    category: 'main',
  },
  {
    id: 'QUIZ-002-9',
    title: 'Вопрос 8: Есть ли дети',
    stepIndex: 8,
    progress: { current: 8, total: MAIN_STEPS_TOTAL },
    component: Quiz002Step9,
    validate: (state) => !!state.answers[8],
    next: () => 'QUIZ-002-10',
    prev: () => 'QUIZ-002-8',
    route: '/quiz/step/8',
    category: 'main',
  },
  {
    id: 'QUIZ-002-10',
    title: 'Вопрос 9: Есть ли другие питомцы',
    stepIndex: 9,
    progress: { current: 9, total: MAIN_STEPS_TOTAL },
    component: Quiz002Step10,
    validate: (state) => !!state.answers[9],
    next: () => 'QUIZ-002-11', // Go to extra questions
    prev: () => 'QUIZ-002-9',
    route: '/quiz/step/9',
    category: 'main',
  },
  
  // ===== EXTRA BLOCK: "Ещё 3 вопроса" =====
  {
    id: 'QUIZ-002-11',
    title: 'Вопрос 10: Размер собаки (1 из 3)',
    stepIndex: 10,
    progress: { current: 1, total: EXTRA_STEPS_TOTAL, label: 'Ещё 3 вопроса' },
    component: Quiz002Step11,
    validate: (state) => !!state.answers[10],
    next: () => 'QUIZ-002-12',
    prev: () => 'QUIZ-002-10',
    route: '/quiz/step/10',
    category: 'extra',
  },
  {
    id: 'QUIZ-002-12',
    title: 'Вопрос 11: Уход за шерстью (2 из 3)',
    stepIndex: 11,
    progress: { current: 2, total: EXTRA_STEPS_TOTAL, label: 'Ещё 3 вопроса' },
    component: Quiz002Step12,
    validate: (state) => !!state.answers[11],
    next: () => 'QUIZ-002-13',
    prev: () => 'QUIZ-002-11',
    route: '/quiz/step/11',
    category: 'extra',
  },
  {
    id: 'QUIZ-002-13',
    title: 'Вопрос 12: Окрас собаки (3 из 3)',
    stepIndex: 12,
    progress: { current: 3, total: EXTRA_STEPS_TOTAL, label: 'Ещё 3 вопроса' },
    component: Quiz002Step13,
    validate: (state) => !!state.answers[12],
    next: () => 'QUIZ-002-14',
    prev: () => 'QUIZ-002-12',
    route: '/quiz/step/12',
    category: 'extra',
  },
  
  // ===== FINAL BLOCK =====
  {
    id: 'QUIZ-002-14',
    title: 'Вопрос 13: Энергия пользователя',
    stepIndex: 13,
    progress: { current: 1, total: FINAL_STEPS_TOTAL, label: 'Финальные вопросы' },
    component: Quiz002Step14,
    validate: (state) => !!state.answers[13],
    next: () => 'QUIZ-002-15',
    prev: () => 'QUIZ-002-13',
    route: '/quiz/step/13',
    category: 'final',
  },
  {
    id: 'QUIZ-002-15',
    title: 'Вопрос 14: Готовность взять питомца с особенностями',
    stepIndex: 14,
    progress: { current: 2, total: FINAL_STEPS_TOTAL, label: 'Финальные вопросы' },
    component: Quiz002Step15,
    validate: (state) => !!state.answers[14],
    next: () => 'QUIZ-002-16',
    prev: () => 'QUIZ-002-14',
    route: '/quiz/step/14',
    category: 'final',
  },
  {
    id: 'QUIZ-002-16',
    title: 'Вопрос 15: Где вы живёте?',
    stepIndex: 15,
    progress: { current: 3, total: FINAL_STEPS_TOTAL, label: 'Финальные вопросы' },
    component: Quiz002Step16,
    validate: (state) => !!state.answers[15],
    next: () => 'QUIZ-003', // Go to processing
    prev: () => 'QUIZ-002-15',
    route: '/quiz/step/15',
    category: 'final',
  },
  
  // ===== SYSTEM: Processing & Results =====
  {
    id: 'QUIZ-003',
    title: 'Обработка результатов',
    stepIndex: null,
    progress: null,
    component: Quiz003Processing,
    validate: () => true,
    next: () => 'QUIZ-004',
    prev: () => null, // No back from processing
    route: '/quiz/processing',
    isSystem: true,
    category: 'system',
  },
  {
    id: 'QUIZ-004',
    title: 'Результаты анкеты',
    stepIndex: null,
    progress: null,
    component: Quiz004Results,
    validate: () => true,
    next: () => null, // End of flow
    prev: () => null, // No back from results
    route: '/quiz/results',
    isSystem: true,
    category: 'system',
  },
  {
    id: 'QUIZ-005',
    title: 'Диалог выхода',
    stepIndex: null,
    progress: null,
    component: Quiz005ExitDialog,
    validate: () => true,
    next: () => null,
    prev: () => null,
    route: '/quiz/exit',
    isSystem: true,
    category: 'system',
  },
];

// ===== Helper Functions =====

/** Get step by ID */
export function getStepById(id: string): QuizStepDefinition | undefined {
  return QUIZ_STEPS_REGISTRY.find(step => step.id === id);
}

/** Get step by route */
export function getStepByRoute(route: string): QuizStepDefinition | undefined {
  return QUIZ_STEPS_REGISTRY.find(step => step.route === route);
}

/** Get step by step index */
export function getStepByIndex(index: number): QuizStepDefinition | undefined {
  return QUIZ_STEPS_REGISTRY.find(step => step.stepIndex === index);
}

/** Get next step ID from current */
export function getNextStepId(currentId: string, state: QuizStepState): string | null {
  const current = getStepById(currentId);
  if (!current) return null;
  return current.next(state);
}

/** Get previous step ID from current */
export function getPrevStepId(currentId: string, state: QuizStepState): string | null {
  const current = getStepById(currentId);
  if (!current) return null;
  return current.prev(state);
}

/** Get all question steps (non-system) */
export function getQuestionSteps(): QuizStepDefinition[] {
  return QUIZ_STEPS_REGISTRY.filter(step => !step.isSystem && step.stepIndex !== null);
}

/** Get total question count */
export function getTotalQuestions(): number {
  return getQuestionSteps().length;
}

/** Map step ID to route */
export function getRouteForStepId(id: string): string | undefined {
  return getStepById(id)?.route;
}

/** Map route to step ID */
export function getStepIdForRoute(route: string): string | undefined {
  return getStepByRoute(route)?.id;
}

/** Get first question step */
export function getFirstQuestionStep(): QuizStepDefinition | undefined {
  return QUIZ_STEPS_REGISTRY.find(step => step.category === 'main' && step.stepIndex === 1);
}

/** Check if step is valid for navigation */
export function canNavigateToStep(stepId: string, state: QuizStepState): boolean {
  const step = getStepById(stepId);
  if (!step) return false;
  
  // System screens are always accessible
  if (step.isSystem) return true;
  
  // Check all previous steps are complete
  const stepIndex = QUIZ_STEPS_REGISTRY.findIndex(s => s.id === stepId);
  for (let i = 0; i < stepIndex; i++) {
    const prevStep = QUIZ_STEPS_REGISTRY[i];
    if (!prevStep.isSystem && prevStep.stepIndex !== null && !prevStep.validate(state)) {
      return false;
    }
  }
  
  return true;
}

export default QUIZ_STEPS_REGISTRY;
